import { Button, Card, FormHelperText, Modal, ModalClose, ModalDialog, Stack, Typography } from '@healthinal/ui';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCheckPersonMatchMutation } from '../../../api/created/personMatchCheck.ts';
import { IPersonListItem } from '../../../api/generated/person-controller/person-controller.ts';
import { checkPersonDuplicate } from '../../../api/generated/person/checkDuplicate.ts';
import { useCreatePersonMutation } from '../../../api/generated/person/createPerson.ts';
import { mapCreatePersonFormToPayload } from '../../../utils/maps/createPersonFormToPayload.ts';
import {
  getCreatedPersonId,
  getDefaultCreatePersonValues,
  getPersonMatchCheckPayload,
  mapCreatedPersonToListItem,
} from '../helpers/createPersonData.ts';
import { DuplicatePersonConfirmationModal } from './DuplicatePersonConfirmationModal.tsx';
import {
  DuplicateCheckField,
  DuplicateErrors,
  PersonDataFormFields,
  PersonDataFormValues,
  getDuplicateCheckPayload,
  getDuplicateErrors,
} from './PersonDataFormFields.tsx';

type FormValues = PersonDataFormValues;

type CreatePersonDataProps = {
  open: boolean;
  onClose: () => void;
  onSubmit?: (data: FormValues) => void | Promise<void>;
  onCreated?: (person: IPersonListItem) => void;
};

export function CreatePersonData({ open, onClose, onSubmit, onCreated }: CreatePersonDataProps) {
  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    getFieldState,
    formState: { errors, isSubmitting, isValid },
  } = useForm<FormValues>({
    defaultValues: getDefaultCreatePersonValues(),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  const { duplicateErrors, checkDuplicateField, resetDuplicateErrors } = useCreatePersonDuplicateCheck(getValues);
  const createPersonMutation = useCreatePersonMutation();
  const checkPersonMatchMutation = useCheckPersonMatchMutation();
  const [duplicatePersonData, setDuplicatePersonData] = useState<FormValues | null>(null);
  const [duplicatePersons, setDuplicatePersons] = useState<IPersonListItem[]>([]);

  const isSaveDisabled =
    !isValid || isSubmitting || createPersonMutation.isPending || checkPersonMatchMutation.isPending;

  const createPerson = async (data: FormValues) => {
    await onSubmit?.(data);

    const payload = mapCreatePersonFormToPayload(data);
    const personId = getCreatedPersonId(await createPersonMutation.mutateAsync(payload));

    onCreated?.(mapCreatedPersonToListItem(data, personId));

    setDuplicatePersonData(null);
    setDuplicatePersons([]);
    resetDuplicateErrors();
    reset(getDefaultCreatePersonValues());
    onClose();
  };

  const handleFormSubmit = async (data: FormValues) => {
    createPersonMutation.reset();
    checkPersonMatchMutation.reset();

    const personMatches = await checkPersonMatchMutation.mutateAsync(getPersonMatchCheckPayload(data));

    if (personMatches.length > 0) {
      setDuplicatePersonData(data);
      setDuplicatePersons(personMatches);
      return;
    }

    await createPerson(data);
  };

  const handleCreateDuplicatePerson = async () => {
    if (!duplicatePersonData) {
      return;
    }

    createPersonMutation.reset();
    await createPerson(duplicatePersonData);
  };

  const handleCloseDuplicateConfirmation = () => {
    setDuplicatePersonData(null);
    setDuplicatePersons([]);
  };

  const handleClose = () => {
    setDuplicatePersonData(null);
    setDuplicatePersons([]);
    resetDuplicateErrors();
    createPersonMutation.reset();
    checkPersonMatchMutation.reset();
    reset(getDefaultCreatePersonValues());
    onClose();
  };

  return (
    <>
      <Modal
        open={open}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          handleClose();
        }}>
        <ModalDialog
          sx={{
            width: 'min(900px, 95vw)',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          }}>
          <ModalClose />

          <Typography level="h3">Person erfassen</Typography>

          <Stack sx={{ flex: 1, overflow: 'auto', mt: 2 }} gap={2}>
            <Card>
              <PersonDataFormFields
                register={register}
                control={control}
                setValue={setValue}
                getValues={getValues}
                getFieldState={getFieldState}
                errors={errors}
                duplicateErrors={duplicateErrors}
                checkDuplicateField={checkDuplicateField}
                referencePersonSelect={{ mode: 'create' }}
                postalCodeEnabled={open}
                showPlaceholders
                showDirtyIndicator={false}
              />
            </Card>

            {(createPersonMutation.isError || checkPersonMatchMutation.isError) && (
              <FormHelperText>Person konnte nicht gespeichert werden</FormHelperText>
            )}
          </Stack>

          <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ pt: 2 }}>
            <Button color="neutral" onClick={handleClose}>
              Abbrechen
            </Button>
            <Button
              loading={isSubmitting || createPersonMutation.isPending || checkPersonMatchMutation.isPending}
              onClick={handleSubmit(handleFormSubmit)}
              disabled={isSaveDisabled}>
              Speichern
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      <DuplicatePersonConfirmationModal
        open={!!duplicatePersonData}
        loading={createPersonMutation.isPending}
        persons={duplicatePersons}
        onCancel={handleCloseDuplicateConfirmation}
        onConfirm={handleCreateDuplicatePerson}
      />
    </>
  );
}

function useCreatePersonDuplicateCheck(getValues: () => FormValues) {
  const [duplicateErrors, setDuplicateErrors] = useState<DuplicateErrors>({});
  const duplicateCheckVersionRef = useRef(0);

  const resetDuplicateErrors = () => {
    duplicateCheckVersionRef.current += 1;
    setDuplicateErrors({});
  };

  const checkDuplicateField = async (field: DuplicateCheckField, value: string | null | undefined) => {
    const payload = getDuplicateCheckPayload(field, value, getValues());

    if (Object.keys(payload).length === 0) {
      resetDuplicateErrors();
      return;
    }

    const checkVersion = (duplicateCheckVersionRef.current += 1);

    try {
      const response = await checkPersonDuplicate(payload);

      if (checkVersion !== duplicateCheckVersionRef.current || !isDuplicatePayloadCurrent(payload, getValues())) {
        return;
      }

      setDuplicateErrors(getDuplicateErrors(payload, response));
    } catch (error) {
      console.error('Duplicate check failed', error);
    }
  };

  return { duplicateErrors, checkDuplicateField, resetDuplicateErrors };
}

function isDuplicatePayloadCurrent(payload: Partial<Record<DuplicateCheckField, string>>, currentValues: FormValues) {
  return Object.entries(payload).every(([key, value]) => {
    const currentValue = currentValues[key as DuplicateCheckField];

    return typeof currentValue === 'string' && currentValue.trim() === value;
  });
}
